/* CasePdfMenu — uniform "View documents" dropdown for case-detail headers.
 *
 * Each case has up to four printable artefacts:
 *   - rx         : Signed prescription PDF (only when the doctor has
 *                  signed it in the wizard — `signed_rx_available` prop)
 *   - submission : Doctor's original submission + prescription bundle
 *   - plan       : Planner's deliverable + iteration history
 *   - dossier    : Combined full-lifecycle dossier (submission + plan + timeline)
 *   - sticker    : 100×50mm thermal label for the production pouch
 *
 * iter101.58 — Replaced the native `<details>` + `<summary>` dropdown
 * with a controlled React component so it:
 *   1. Closes when the user clicks anywhere outside the menu.
 *   2. Closes as soon as an item is selected (native `<a target=_blank>`
 *      doesn't collapse a `<details>` — the menu used to stay pinned
 *      open until the next explicit toggle).
 *   3. Closes on `Escape`.
 * The `Authorization` header can't be attached to a top-level navigation
 * triggered by `<a target="_blank">`, so each item still uses `?auth=`.
 *
 * Variants let callers filter which docs are offered: the Planner sees
 * Rx + Submission + Plan + Dossier (Rx when signed); the Admin sees all;
 * specific portals can pass `omit={["dossier"]}` etc. when needed.
 *
 * The Rx entry uses a different endpoint shape — `/cases/{id}/prescription/pdf`
 * instead of `/cases/{id}/pdf/{key}` — so it's resolved via the `href`
 * field on the item rather than the standard `{key}` template.
 */
import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { FileArrowUp, CaretDown } from "@phosphor-icons/react";

const ITEMS = [
    {
        key: "submission",
        title: "Submission & Prescription",
        sub: "What the doctor originally sent in",
    },
    {
        key: "plan",
        title: "Treatment Plan",
        sub: "Planner's deliverable + iterations",
    },
    {
        key: "dossier",
        title: "Full Case Dossier",
        sub: "Submission + Plan + Timeline",
    },
    {
        key: "sticker",
        title: "Sticker label",
        sub: "100×50mm thermal label — case + patient",
    },
];

export default function CasePdfMenu({
    caseId,
    omit = [],
    label = "Download PDF",
    testIdPrefix = "case-pdf",
    align = "right",
    signedRxAvailable = false,
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    /* Close on outside click / Escape. */
    useEffect(() => {
        if (!open) return undefined;
        const handleClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("touchstart", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("touchstart", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    if (!caseId) return null;
    const token = localStorage.getItem("ca_token") || "";

    /* iter98.99by — Prepend a signed-Rx item when the doctor has signed
       the prescription in the wizard. Different endpoint shape —
       `/cases/{id}/prescription/pdf` — so we build its href explicitly
       and tag it with `customHref: true` so the renderer skips the
       default `/pdf/{key}` template. */
    const rxItem = signedRxAvailable && !omit.includes("rx") ? [{
        key: "rx",
        title: "Signed Prescription",
        sub: "Doctor's signed Rx PDF",
        customHref: `${API}/cases/${caseId}/prescription/pdf?auth=${token}`,
    }] : [];
    const items = [...rxItem, ...ITEMS.filter((i) => !omit.includes(i.key))];

    return (
        <div
            ref={rootRef}
            className="relative inline-block"
            data-testid={`${testIdPrefix}-menu`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="list-none inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer select-none"
                title="Open or print the case's documents"
                aria-expanded={open}
                aria-haspopup="menu"
                data-testid={`${testIdPrefix}-menu-toggle`}
            >
                <FileArrowUp size={12} weight="bold" /> {label}
                <CaretDown
                    size={10}
                    weight="bold"
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div
                    role="menu"
                    className={`absolute ${align === "left" ? "left-0" : "right-0"} top-full mt-1 z-30 min-w-[240px] rounded-xl border border-border bg-card shadow-lg overflow-hidden`}
                >
                    {items.map((it, idx) => (
                        <a
                            key={it.key}
                            href={it.customHref || `${API}/cases/${caseId}/pdf/${it.key}?auth=${token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="menuitem"
                            onClick={() => setOpen(false)}
                            className={`block px-4 py-2.5 text-xs hover:bg-secondary ${idx > 0 ? "border-t border-border" : ""}`}
                            data-testid={`${testIdPrefix}-download-${it.key}`}
                        >
                            <div className="font-semibold text-foreground">
                                {it.title}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                                {it.sub}
                            </div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}
